import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { GlobalEntityAutocomplete, EntityOption } from './GlobalEntityAutocomplete';
import { TestCase } from '../types';

interface TestAutoCompleteProps {
  projectId: string;
  label: string;
  placeholder?: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentEntityId?: string;
  currentEntityType?: 'URS' | 'FS' | 'RISK' | 'SOP';
}

export const TestAutoComplete: React.FC<TestAutoCompleteProps> = ({
  projectId,
  label,
  placeholder,
  selectedIds,
  onChange,
  currentEntityId,
  currentEntityType
}) => {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [, setLoading] = useState(true);
  const [readOnlyIds, setReadOnlyIds] = useState<string[]>([]);

  useEffect(() => {
    const loadTests = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'tests');
        const snapshot = await getDocs(col);
        const loaded: EntityOption[] = snapshot.docs.map(doc => {
          const data = doc.data() as TestCase;
          return {
            id: data.id || `TEST-${doc.id.substring(0, 8)}`,
            label: data.title || data.id || `TEST-${doc.id.substring(0, 8)}`,
            title: data.title
          };
        });
        setOptions(loaded);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading Tests for autocomplete:', err);
      }
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadTests();
  }, [projectId]);

  // Query for reverse relationships
  useEffect(() => {
    const queryReverseLinks = async () => {
      if (!projectId || !currentEntityId || !currentEntityType) {
        setReadOnlyIds([]);
        return;
      }

      try {
        const reverseIds: string[] = [];
        const testCol = collection(db, 'projects', projectId, 'tests');
        const testSnapshot = await getDocs(testCol);
        testSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const links = data.links || {};
          
          if (currentEntityType === 'URS' && links.urs_ids?.includes(currentEntityId)) {
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'FS' && links.fs_ids?.includes(currentEntityId)) {
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'RISK' && links.risk_ids?.includes(currentEntityId)) {
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'SOP' && links.sop_ids?.includes(currentEntityId)) {
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          }
        });

        setReadOnlyIds(reverseIds);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error querying reverse links for TEST:', err);
      }
        setReadOnlyIds([]);
      }
    };

    queryReverseLinks();
  }, [projectId, currentEntityId, currentEntityType]);

  return (
    <GlobalEntityAutocomplete
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      options={options}
      onChange={onChange}
      readOnlyIds={readOnlyIds}
    />
  );
};

